import React, { useState, useEffect } from "react";
import TagModal from "../../components/TagModal";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Paper,
  Tooltip,
  Divider,
  Box,
  Collapse,
  Chip,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import GetAppIcon from "@material-ui/icons/GetApp";
import PublishIcon from "@material-ui/icons/Publish";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ViewColumnIcon from "@material-ui/icons/ViewColumn";
import BusinessIcon from "@material-ui/icons/Business";
import FilterListIcon from "@material-ui/icons/FilterList";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import SettingsIcon from "@material-ui/icons/Settings";
import CloseIcon from "@material-ui/icons/Close";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { toast } from "react-toastify";
import { setKanbanLaneOrder } from "../../services/companyKanbanService";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import BrandModal from "../BrandModal";
import KanbanBoardModal from "../KanbanBoardModal";
import { getStandardButtonStyles } from "../../styles/standardButtons";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  dialogPaper: {
    backgroundColor: theme.palette.type === "dark" ? "#1E2126" : "#FFFFFF",
    borderRadius: 16,
    border: `1px solid ${theme.palette.primary.main}40`,
    boxShadow: "0 16px 48px rgba(0, 0, 0, 0.45)",
    color: theme.palette.type === "dark" ? "#E2E8F0" : theme.palette.text.primary,
    overflow: "hidden",
  },
  dialogHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    "& h2": {
      display: "flex",
      alignItems: "center",
      fontSize: "1.15rem",
      fontWeight: 600,
      margin: 0,
      color: theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.text.primary,
    },
  },
  helpBox: {
    padding: theme.spacing(1.5, 3),
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(0, 180, 216, 0.08)" : "rgba(0, 180, 216, 0.05)",
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(0, 180, 216, 0.2)" : "rgba(0, 180, 216, 0.15)"
    }`,
  },
  dialogContent: {
    padding: theme.spacing(2.5, 3),
    maxHeight: "75vh",
    overflowY: "auto",
  },
  dialogActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
    }`,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.02)",
    gap: 8,
  },
  actionOutlineBtn: {
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    fontSize: "0.82rem",
    border: `1px solid ${theme.palette.primary.main}50`,
    color: theme.palette.type === "dark" ? theme.palette.primary.light || theme.palette.primary.main : theme.palette.primary.dark,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}15`,
      borderColor: theme.palette.primary.main,
    },
  },
}));

const KanbanAdminModal = ({
  open,
  onClose,
  brands,
  boards,
  reloadKanban,
  onExport,
  onImport,
  fileInputRef,
  handleFileChange,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";
  const borderColor = isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";
  const boardBg = isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)";
  const laneBg = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)";
  const iconColor = isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)";

  const [localBrands, setLocalBrands] = useState(brands || []);
  const [localBoards, setLocalBoards] = useState(boards || []);
  const [showHelp, setShowHelp] = useState(false);

  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState(null);

  const [boardModalOpen, setBoardModalOpen] = useState(false);
  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const [boardBrandId, setBoardBrandId] = useState(null);
  const [collapsedBrands, setCollapsedBrands] = useState({});
  const toggleBrand = (id) =>
    setCollapsedBrands((prev) => ({ ...prev, [id]: prev[id] === false ? true : false }));

  const [collapsedBoards, setCollapsedBoards] = useState({});
  const toggleBoard = (id) =>
    setCollapsedBoards((prev) => ({ ...prev, [id]: prev[id] === false ? true : false }));

  const [collapsedLanes, setCollapsedLanes] = useState({});
  const toggleLane = (id) =>
    setCollapsedLanes((prev) => ({ ...prev, [id]: prev[id] === false ? true : false }));

  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [selectedTagId, setSelectedTagId] = useState(null);
  const [selectedBoardForTag, setSelectedBoardForTag] = useState(null);

  const [hasChanged, setHasChanged] = useState(false);

  const fetchHierarchy = async () => {
    try {
      const [brandsRes, boardsRes] = await Promise.all([
        api.get("/brands"),
        api.get("/kanban-boards", { params: { t: new Date().getTime() } }),
      ]);
      setLocalBrands(brandsRes.data.brands || brandsRes.data);
      setLocalBoards(boardsRes.data);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    if (open) {
      fetchHierarchy();
    }
  }, [open]);

  const handleDataChanged = () => {
    setHasChanged(true);
    fetchHierarchy();
  };

  const [importBrandId, setImportBrandId] = useState(null);

  const handleImportFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !importBrandId) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const json = JSON.parse(e.target.result);
        const payload = {
          ...json,
          brandId: importBrandId,
        };
        await api.post("/kanban-boards/import", payload);
        toast.success(
          i18n.t("kanban.messages.importSuccess") || "Embudo importado exitosamente"
        );
        fetchHierarchy();
        setHasChanged(true);
      } catch (err) {
        toastError(err);
      }
    };
    reader.readAsText(file);
    e.target.value = null; // reset
  };

  const handleExportBoard = async (boardId, boardName) => {
    try {
      const { data } = await api.get(`/kanban-boards/${boardId}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kanban-export-${boardName}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err);
    }
  };

  const handleAddBrand = () => {
    setSelectedBrandId(null);
    setBrandModalOpen(true);
  };

  const handleEditBrand = (brandId) => {
    setSelectedBrandId(brandId);
    setBrandModalOpen(true);
  };

  const handleAddBoard = (brandId) => {
    setBoardBrandId(brandId);
    setSelectedBoardId(null);
    setBoardModalOpen(true);
  };

  const handleEditBoard = (brandId, boardId) => {
    setBoardBrandId(brandId);
    setSelectedBoardId(boardId);
    setBoardModalOpen(true);
  };

  const handleAddLane = (boardId) => {
    setSelectedTagId(null);
    setSelectedBoardForTag(boardId);
    setCollapsedBoards((prev) => ({ ...prev, [boardId]: false }));
    setTagModalOpen(true);
  };

  const handleEditLane = (laneId, boardId) => {
    setSelectedTagId(laneId);
    setSelectedBoardForTag(boardId);
    setCollapsedBoards((prev) => ({ ...prev, [boardId]: false }));
    setTagModalOpen(true);
  };

  const handleDeleteLane = async (laneId) => {
    if (window.confirm(i18n.t("kanban.adminModal.confirmDeleteStage"))) {
      try {
        await api.delete(`/tags/${laneId}`);
        handleDataChanged();
      } catch (err) {
        toastError(err);
      }
    }
  };

  const handleDeleteBoard = async (boardId) => {
    if (window.confirm(i18n.t("kanban.adminModal.confirmDeleteFunnel"))) {
      try {
        await api.delete(`/kanban-boards/${boardId}`);
        handleDataChanged();
      } catch (err) {
        toastError(err);
      }
    }
  };

  const handleMoveLane = async (boardId, laneIndex, direction) => {
    const targetBoard = localBoards.find((b) => b.id === boardId);
    if (!targetBoard || !Array.isArray(targetBoard.tags)) return;

    const targetIndex = laneIndex + direction;
    if (targetIndex < 0 || targetIndex >= targetBoard.tags.length) return;

    const newTags = Array.from(targetBoard.tags);
    const [movedLane] = newTags.splice(laneIndex, 1);
    newTags.splice(targetIndex, 0, movedLane);

    setLocalBoards((prev) =>
      prev.map((b) => (b.id === boardId ? { ...b, tags: newTags } : b))
    );
    setHasChanged(true);

    try {
      const newLaneOrder = ["lane0", ...newTags.map((t) => String(t.id))];
      await setKanbanLaneOrder(newLaneOrder, boardId);
      toast.success(i18n.t("kanban.adminModal.orderUpdated"));
    } catch (err) {
      console.error("Error al reordenar etapas:", err);
      toastError(err);
      fetchHierarchy();
    }
  };

  const handleCloseMain = () => {
    setShowHelp(false);
    onClose();
    if (hasChanged && reloadKanban) reloadKanban();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseMain}
        fullWidth
        maxWidth="md"
        PaperProps={{
          className: classes.dialogPaper,
        }}
      >
        <div className={classes.dialogHeader}>
          <h2>
            <SettingsIcon
              style={{
                marginRight: 10,
                fontSize: 22,
                color: theme.palette.primary.main,
              }}
            />
            {i18n.t("kanban.adminModal.title")}
          </h2>
          <Box display="flex" alignItems="center">
            <IconButton
              size="small"
              onClick={() => setShowHelp((prev) => !prev)}
              title="Ayuda / Información"
              style={{ marginRight: 8, color: showHelp ? "#00b4d8" : "inherit" }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
            <Typography
              variant="caption"
              style={{ opacity: 0.6, marginRight: 12, fontWeight: 500 }}
            >
              BotMix | CRM
            </Typography>
            <IconButton size="small" onClick={handleCloseMain} aria-label="Cerrar">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </div>

        <Collapse in={showHelp}>
          <div className={classes.helpBox}>
            <Typography variant="body2" style={{ lineHeight: 1.5, opacity: 0.9 }}>
              ℹ️ {i18n.t("kanban.adminModal.summary")}
            </Typography>
          </div>
        </Collapse>

        <DialogContent dividers className={classes.dialogContent}>
          <div style={{ marginBottom: 16 }}>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddBrand}
              className={classes.applyButton}
            >
              {i18n.t("kanban.adminModal.newBrand")}
            </Button>
            <input
              style={{ display: "none" }}
              id="admin-import-board-local"
              type="file"
              accept=".json"
              onChange={handleImportFileChange}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {localBrands.map((brand) => {
              const brandBoards = localBoards.filter((b) => b.brandId === brand.id);
              return (
                <Paper
                  key={brand.id}
                  style={{
                    padding: "10px 16px",
                    borderRadius: 10,
                    border: `1px solid ${borderColor}`,
                    backgroundColor: theme.palette.background.paper,
                  }}
                  elevation={0}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: collapsedBrands[brand.id] !== false ? 0 : 8,
                    }}
                  >
                    <div
                      onClick={() => toggleBrand(brand.id)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flex: 1,
                        cursor: "pointer",
                        userSelect: "none",
                      }}
                    >
                      {collapsedBrands[brand.id] !== false ? (
                        <ChevronRightIcon
                          style={{ marginRight: 6, color: iconColor }}
                        />
                      ) : (
                        <ExpandMoreIcon
                          style={{ marginRight: 6, color: iconColor }}
                        />
                      )}
                      <Typography
                        variant="subtitle1"
                        style={{
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <BusinessIcon style={{ color: "#3498db" }} /> {brand.name}
                      </Typography>
                    </div>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                      <Tooltip title={i18n.t("kanban.adminModal.editBrand")}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditBrand(brand.id)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={i18n.t("kanban.adminModal.importFunnelToBrand")}>
                        <label htmlFor="admin-import-board-local" style={{ margin: 0 }}>
                          <IconButton
                            size="small"
                            component="span"
                            onClick={() => setImportBrandId(brand.id)}
                            style={{ border: `1px solid ${borderColor}` }}
                          >
                            <PublishIcon
                              style={{ fontSize: "1.2rem", color: "#4caf50" }}
                            />
                          </IconButton>
                        </label>
                      </Tooltip>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => handleAddBoard(brand.id)}
                        className={classes.actionOutlineBtn}
                      >
                        {i18n.t("kanban.adminModal.newFunnel")}
                      </Button>
                    </div>
                  </div>

                  {collapsedBrands[brand.id] !== false ? null : (
                    <>
                      <Divider style={{ marginBottom: 10, marginTop: 4 }} />
                      {brandBoards.length === 0 ? (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          style={{ padding: "8px 0" }}
                        >
                          {i18n.t("kanban.adminModal.noFunnels")}
                        </Typography>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          {brandBoards.map((board) => (
                            <div
                              key={board.id}
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                background: boardBg,
                                borderRadius: 8,
                                border: `1px solid ${borderColor}`,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  padding: "8px 14px",
                                }}
                              >
                                <div
                                  onClick={() => toggleBoard(board.id)}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    flex: 1,
                                    cursor: "pointer",
                                    userSelect: "none",
                                  }}
                                >
                                  {collapsedBoards[board.id] !== false ? (
                                    <ChevronRightIcon
                                      style={{
                                        marginRight: 8,
                                        color: iconColor,
                                        fontSize: "1.2rem",
                                      }}
                                    />
                                  ) : (
                                    <ExpandMoreIcon
                                      style={{
                                        marginRight: 8,
                                        color: iconColor,
                                        fontSize: "1.2rem",
                                      }}
                                    />
                                  )}
                                  <Typography
                                    variant="body2"
                                    style={{
                                      fontWeight: 600,
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <FilterListIcon
                                      style={{
                                        color: "#e67e22",
                                        marginRight: 8,
                                        fontSize: "1.2rem",
                                      }}
                                    />
                                    {board.name}
                                  </Typography>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                  <Tooltip title={i18n.t("kanban.adminModal.newStage")}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleAddLane(board.id)}
                                    >
                                      <AddIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={i18n.t("kanban.adminModal.editFunnel")}>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleEditBoard(brand.id, board.id)
                                      }
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={i18n.t("kanban.adminModal.exportFunnel")}>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleExportBoard(board.id, board.name)
                                      }
                                    >
                                      <GetAppIcon
                                        style={{ fontSize: "1.2rem", color: "#1976d2" }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={i18n.t("kanban.adminModal.deleteFunnel")}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteBoard(board.id)}
                                      style={{ color: "#e74c3c" }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </div>
                              </div>

                              {collapsedBoards[board.id] !== false ? null : (
                                <div
                                  style={{
                                    paddingLeft: 24,
                                    paddingRight: 14,
                                    paddingBottom: 8,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 6,
                                  }}
                                >
                                  {(!board.tags || board.tags.length === 0) && (
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                      style={{ paddingLeft: 8 }}
                                    >
                                      {i18n.t("kanban.adminModal.noStages")}
                                    </Typography>
                                  )}
                                  {board.tags &&
                                    board.tags.map((lane, laneIndex) => (
                                      <div
                                        key={lane.id}
                                        style={{
                                          display: "flex",
                                          flexDirection: "column",
                                          background: laneBg,
                                          borderRadius: 6,
                                          borderLeft: `4px solid ${lane.color}`,
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            padding: "6px 10px",
                                          }}
                                        >
                                          <div
                                            onClick={() => toggleLane(lane.id)}
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              flex: 1,
                                              cursor: "pointer",
                                              userSelect: "none",
                                            }}
                                          >
                                            {collapsedLanes[lane.id] !== false ? (
                                              <ChevronRightIcon
                                                style={{
                                                  marginRight: 4,
                                                  color: iconColor,
                                                  fontSize: "1rem",
                                                }}
                                              />
                                            ) : (
                                              <ExpandMoreIcon
                                                style={{
                                                  marginRight: 4,
                                                  color: iconColor,
                                                  fontSize: "1rem",
                                                }}
                                              />
                                            )}
                                            <Typography
                                              variant="body2"
                                              style={{
                                                fontWeight: 600,
                                                display: "flex",
                                                alignItems: "center",
                                              }}
                                            >
                                              <ViewColumnIcon
                                                style={{
                                                  color: lane.color,
                                                  marginRight: 6,
                                                  fontSize: "1.1rem",
                                                }}
                                              />
                                              {lane.name}
                                              {lane.stageRole === "won" && (
                                                <Chip
                                                  size="small"
                                                  label={i18n.t("tagModal.stageRoles.wonBadge")}
                                                  style={{
                                                    marginLeft: 8,
                                                    height: 20,
                                                    fontSize: "0.72rem",
                                                    fontWeight: 700,
                                                    backgroundColor: "rgba(46, 204, 113, 0.15)",
                                                    color: "#2ecc71",
                                                    border: "1px solid rgba(46, 204, 113, 0.4)",
                                                  }}
                                                />
                                              )}
                                              {lane.stageRole === "lost" && (
                                                <Chip
                                                  size="small"
                                                  label={i18n.t("tagModal.stageRoles.lostBadge")}
                                                  style={{
                                                    marginLeft: 8,
                                                    height: 20,
                                                    fontSize: "0.72rem",
                                                    fontWeight: 700,
                                                    backgroundColor: "rgba(239, 68, 68, 0.15)",
                                                    color: "#ef4444",
                                                    border: "1px solid rgba(239, 68, 68, 0.4)",
                                                  }}
                                                />
                                              )}
                                            </Typography>
                                          </div>
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 2,
                                            }}
                                          >
                                            <Tooltip
                                              title={i18n.t("kanban.adminModal.moveUp")}
                                            >
                                              <span>
                                                <IconButton
                                                  size="small"
                                                  disabled={laneIndex === 0}
                                                  onClick={() =>
                                                    handleMoveLane(
                                                      board.id,
                                                      laneIndex,
                                                      -1
                                                    )
                                                  }
                                                >
                                                  <ArrowUpwardIcon
                                                    style={{
                                                      fontSize: "1rem",
                                                      color:
                                                        laneIndex === 0
                                                          ? undefined
                                                          : "#2196f3",
                                                    }}
                                                  />
                                                </IconButton>
                                              </span>
                                            </Tooltip>
                                            <Tooltip
                                              title={i18n.t(
                                                "kanban.adminModal.moveDown"
                                              )}
                                            >
                                              <span>
                                                <IconButton
                                                  size="small"
                                                  disabled={
                                                    laneIndex ===
                                                    board.tags.length - 1
                                                  }
                                                  onClick={() =>
                                                    handleMoveLane(board.id, laneIndex, 1)
                                                  }
                                                >
                                                  <ArrowDownwardIcon
                                                    style={{
                                                      fontSize: "1rem",
                                                      color:
                                                        laneIndex ===
                                                        board.tags.length - 1
                                                          ? undefined
                                                          : "#2196f3",
                                                    }}
                                                  />
                                                </IconButton>
                                              </span>
                                            </Tooltip>
                                            <Tooltip
                                              title={i18n.t(
                                                "kanban.adminModal.editStage"
                                              )}
                                            >
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  handleEditLane(lane.id, board.id)
                                                }
                                              >
                                                <EditIcon style={{ fontSize: "1rem" }} />
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip
                                              title={i18n.t(
                                                "kanban.adminModal.deleteStage"
                                              )}
                                            >
                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  handleDeleteLane(lane.id)
                                                }
                                                style={{ color: "#e74c3c" }}
                                              >
                                                <DeleteIcon
                                                  style={{ fontSize: "1rem" }}
                                                />
                                              </IconButton>
                                            </Tooltip>
                                          </div>
                                        </div>

                                        {collapsedLanes[lane.id] !== false ? null : (
                                          <div
                                            style={{
                                              paddingLeft: 24,
                                              paddingBottom: 8,
                                              display: "flex",
                                              flexWrap: "wrap",
                                              gap: 6,
                                            }}
                                          >
                                            {(!lane.kanbanLaneTags ||
                                              lane.kanbanLaneTags.length === 0) && (
                                              <Typography
                                                variant="caption"
                                                color="textSecondary"
                                              >
                                                {i18n.t("kanban.adminModal.noSubtags")}
                                              </Typography>
                                            )}
                                            {lane.kanbanLaneTags &&
                                              lane.kanbanLaneTags
                                                .map((klt) => klt.tag)
                                                .filter((t) => t)
                                                .map((tag) => (
                                                  <div
                                                    key={tag.id}
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      background: tag.color,
                                                      color: "#fff",
                                                      padding: "2px 8px",
                                                      borderRadius: 12,
                                                      fontSize: "0.72rem",
                                                      fontWeight: 600,
                                                    }}
                                                  >
                                                    <LocalOfferIcon
                                                      style={{
                                                        fontSize: "0.8rem",
                                                        marginRight: 4,
                                                        color: "#FFD700",
                                                      }}
                                                    />
                                                    {tag.name}
                                                  </div>
                                                ))}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </Paper>
              );
            })}

            {localBrands.length === 0 && (
              <Typography
                variant="body1"
                color="textSecondary"
                style={{ textAlign: "center", padding: 24 }}
              >
                {i18n.t("kanban.adminModal.noBrands")}
              </Typography>
            )}
          </div>
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button onClick={handleCloseMain} className={classes.dangerButton}>
            {i18n.t("kanban.adminModal.close")}
          </Button>
        </DialogActions>
      </Dialog>

      {brandModalOpen && (
        <BrandModal
          open={brandModalOpen}
          onClose={() => setBrandModalOpen(false)}
          brandId={selectedBrandId}
          onSave={handleDataChanged}
        />
      )}

      {tagModalOpen && (
        <TagModal
          open={tagModalOpen}
          onClose={() => setTagModalOpen(false)}
          tagId={selectedTagId}
          boardId={selectedBoardForTag}
          kanban={1}
          reload={handleDataChanged}
          onSave={handleDataChanged}
        />
      )}

      {boardModalOpen && (
        <KanbanBoardModal
          open={boardModalOpen}
          onClose={() => setBoardModalOpen(false)}
          boardId={selectedBoardId}
          brandId={boardBrandId}
          onSave={handleDataChanged}
        />
      )}
    </>
  );
};

export default KanbanAdminModal;
